// REFACTORED: 2026-02-02
// FROM src/components/modules/ResourceAnalytics.tsx
import { format, parse, isValid } from 'date-fns';
import { alpha } from '@mui/material';
import { SEARCE_COLORS } from '../theme';


// Format currency function
export const formatCurrency = (amount: number): string => {
    if (amount >= 1000000) {
        return `$${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
        return `$${(amount / 1000).toFixed(1)}K`;
    }
    return `$${amount.toFixed(0)}`;
};

// Format number function (with commas)
export const formatNumber = (num: number): string => {
    if (num === null || num === undefined) return '0';
    return new Intl.NumberFormat().format(Math.round(num));
};

// Format percentage function
export const formatPercentage = (num: number): string => {
    if (num === null || num === undefined) return '0%';
    return `${num.toFixed(1)}%`;
};

// Format number with locale (no maximum fraction digits restriction)
export const formatNumberWithLocale = (num: number, maximumFractionDigits: number = 0): string => {
    return num.toLocaleString(undefined, { maximumFractionDigits });
};

// REFACTORED: 2026-02-02
// FROM src/components/project/Projects.tsx

/**
 * Get selected text for dropdown with truncation
 * Used in multi-select dropdowns to display selected values compactly
 */
export const getSelectedText = (selectedValues: string[], allOptions: string[]): string => {
    // Add safety checks for undefined or null values
    if (
        !selectedValues ||
        !Array.isArray(selectedValues) ||
        selectedValues.length === 0
    )
        return "";
    if (!allOptions || !Array.isArray(allOptions)) return "";

    if (selectedValues.length === allOptions.length)
        return `${selectedValues.length} selected`;
    if (selectedValues.length <= 2) {
        // Truncate long individual items to prevent overflow
        const truncatedValues = selectedValues.map((value) =>
            value && value.length > 25 ? value.substring(0, 22) + "..." : value
        );
        return truncatedValues.join(", ");
    }
    return `${selectedValues.length} selected`;
};

// REFACTORED: 2026-02-02
// FROM src/components/project/ProjectAnalytics.tsx

/**
 * Truncate account name for display
 * Limits long account names to 20 characters with ellipsis
 */
export const truncateAccountName = (name: string): string => {
    if (!name || name === 'undefined' || name === 'null') return '';
    const nameStr = String(name);
    return nameStr.length > 20 ? nameStr.substring(0, 20) + "..." : nameStr;
};

// REFACTORED: 2026-02-02
// FROM src/components/project/ProjectDetails.tsx

/**
 * Extract PR number from project code
 * e.g., "PR-100" -> 100, "PR-1234" -> 1234
 */
export const extractPRNumber = (projectCode: string | null | undefined): number => {
    if (!projectCode) return 0;
    const match = projectCode.match(/PR-(\d+)/i);
    return match ? parseInt(match[1], 10) : 0;
};

/**
 * Format date with IST timezone (MM/DD/YYYY)
 */
export const formatDate = (dateString: string | null): string => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
        timeZone: "Asia/Kolkata", // IST timezone
    });
};

/**
 * Format date enhanced with IST timezone (DD-MMM-YY)
 * e.g., "09-Dec-25"
 */
export const formatDateEnhanced = (dateString: string | null): string => {
    if (!dateString) return "-";

    const date = new Date(dateString);

    // Safety check if the string provided is not a valid date
    if (isNaN(date.getTime())) return "-";

    // Use 'en-GB' because it defaults to the order: Day Month Year
    const formatted = date.toLocaleDateString("en-GB", {
        day: "2-digit",   // dd (e.g., 09)
        month: "short",   // MMM (e.g., Dec)
        year: "2-digit",  // yy (e.g., 25)
        timeZone: "Asia/Kolkata",
    });

    // 'en-GB' usually outputs "09 Dec 25", so we replace spaces with dashes
    return formatted.replace(/ /g, "-");
};

/**
 * Format efforts deviation for display
 * Backend returns string values: 'No Deviation', 'Over', 'Under'
 */
export const formatEffortsDeviation = (value: string | number | null | undefined): string => {
    if (value === null || value === undefined) return "-";
    
    // If it's a number, convert to backend format
    if (typeof value === 'number') {
        if (value === 0) return "No Deviation";
        return value > 0 ? "Over" : "Under";
    }
    
    // If it's already a string, return as-is
    return value;
};

/**
 * Validate if a value is a valid repository link
 * Only http/https URLs are considered valid
 */
export const isValidRepositoryLink = (v: unknown): v is string =>
    typeof v === "string" && v.trim().length > 0 && (v.startsWith("http://") || v.startsWith("https://"));

// ============================================================================
// MY INSIGHTS - Utility Functions
// ============================================================================

/**
 * Parse date string to Date object with multiple format support
 */
export const parseDateString = (dateStr: string): Date | null => {
  if (!dateStr) return null;

  // Try different date formats
  const formats = [
    'yyyy-MM-dd',
    'MM/dd/yyyy',
    'dd/MM/yyyy',
    'yyyy/MM/dd',
    'MM-dd-yyyy',
    'dd-MM-yyyy',
    'dd-Mon-yyyy'
  ];

  for (const fmt of formats) {
    try {
      const parsed = parse(dateStr, fmt, new Date());
      if (isValid(parsed)) {
        return parsed;
      }
    } catch (e) {
      // Continue to next format
    }
  }

  // Try native Date parsing as fallback
  const nativeDate = new Date(dateStr);
  return isValid(nativeDate) ? nativeDate : null;
};

/**
 * Calculate DRI Tier from score
 * >=50 Tier 1, >=35 Tier 2, >=20 Tier 3, else Tier 4
 */
export const calculateDriTier = (driScore: number): number => {
  if (driScore >= 50) return 1;
  if (driScore >= 35) return 2;
  if (driScore >= 20) return 3;
  return 4;
};

/**
 * Alias for calculateDriTier for backward compatibility
 */
export const calculateTier = calculateDriTier;

/**
 * 3-way color band for a single product's DRI score, reusing calculateDriTier's 50/35 breakpoints:
 * green = maxed out (Tier 1), amber = near-maxed (Tier 2), red = far from maxed (Tier 3/4).
 */
export const driProductColorSx = (totalDri: number): { bgcolor: string; color: string } => {
  const tier = calculateDriTier(totalDri);
  if (tier === 1) return { bgcolor: alpha(SEARCE_COLORS.success, 0.18), color: SEARCE_COLORS.success_700 };
  if (tier === 2) return { bgcolor: alpha(SEARCE_COLORS.warning, 0.2), color: SEARCE_COLORS.warning_700 };
  return { bgcolor: alpha(SEARCE_COLORS.error, 0.14), color: SEARCE_COLORS.errorDark };
};

/**
 * Format date as DD-Mon-YYYY (e.g., 15-Jan-2024)
 */
export const formatDateDDMonYYYY = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  try {
    const date = parseDateString(dateString);
    if (!date || !isValid(date)) return '-';
    return format(date, 'dd-MMM-yyyy');
  } catch (error) {
    return '-';
  }
};

/**
 * Format number with locale and specific decimal places
 * Used for precise number formatting with 0-2 decimal places
 */
export const formatNumberHelper = (num: number | null | undefined): string => {
  if (num === null || num === undefined) return '0';
  return num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

function formatScaledUnit(abs: number, divisor: number, suffix: string): string {
  const v = abs / divisor;
  const rounded = v >= 100 ? Math.round(v) : Math.round(v * 10) / 10;
  const s = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1).replace(/\.0$/, '');
  return `${s}${suffix}`;
}

/**
 * Hours / counts with K (thousands) and M (millions), rounded for readability (My Insights, utilization charts).
 */
export const formatCompactAbbrev = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '0';
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  const sign = n < 0 ? '-' : '';
  const x = Math.abs(n);
  if (x >= 1_000_000) return `${sign}${formatScaledUnit(x, 1_000_000, 'M')}`;
  if (x >= 1000) return `${sign}${formatScaledUnit(x, 1000, 'K')}`;
  return `${sign}${Math.round(x)}`;
};

/**
 * Currency-style compact display ($ + K / M), rounded (My / Team project insights tables).
 */
export const formatCurrencyCompactAbbrev = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined) return '$0';
  const n = Number(amount);
  if (!Number.isFinite(n)) return '$0';
  const sign = n < 0 ? '-$' : '$';
  const x = Math.abs(n);
  if (x >= 1_000_000) return `${sign}${formatScaledUnit(x, 1_000_000, 'M')}`;
  if (x >= 1000) return `${sign}${formatScaledUnit(x, 1000, 'K')}`;
  return `${sign}${Math.round(x)}`;
};

/** Whole-number percentage string (e.g. My Insights cards). */
export const formatPercentageWhole = (num: number | null | undefined): string => {
  if (num === null || num === undefined || !Number.isFinite(Number(num))) return '0%';
  return `${Math.round(Number(num))}%`;
};

/** Full-precision number for hover tooltips when display is rounded or abbreviated. */
export function formatNumberFull(num: number | null | undefined): string {
  if (num === null || num === undefined || !Number.isFinite(Number(num))) return '';
  return Number(num).toLocaleString(undefined, { maximumFractionDigits: 10 });
}

/** Full currency for hover tooltips (no K/M abbreviation). */
export function formatCurrencyFull(amount: number | null | undefined): string {
  if (amount === null || amount === undefined || !Number.isFinite(Number(amount))) return '';
  return Number(amount).toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Full percentage for hover tooltips (more decimal places than display). */
export function formatPercentageFull(num: number | null | undefined): string {
  if (num === null || num === undefined || !Number.isFinite(Number(num))) return '';
  return `${Number(num).toLocaleString(undefined, { maximumFractionDigits: 10 })}%`;
}

/** True when a tooltip should show the unrounded / full numeric value. */
export function shouldShowNumericFullTooltip(display: string, full: string): boolean {
  const d = display.trim();
  const f = full.trim();
  return f.length > 0 && d !== f;
}
